import { NavigateFunction } from 'react-router-dom';
import api from '../../../api/api';
import { errorHandler } from '../../../utils/helper';
import { successNotification } from '../Toast/Toast';
import raiseClaimIcon from '../../../assets/icons/quick-links/raise-claim.svg';
import superTopupIcon from '../../../assets/icons/quick-links/super-top-up.svg';
import giftCardsIcon from '../../../assets/icons/quick-links/gift-cards.svg';
import curatedDealsIcon from '../../../assets/icons/quick-links/curated-deals.svg';
import buyMedicinesIcon from '../../../assets/icons/quick-links/buy-medicines.svg';
import bookLabTestIcon from '../../../assets/icons/quick-links/book-lab-test.svg';
import consultDoctorIcon from '../../../assets/icons/quick-links/consult-doctor.svg';

/**
 * Quick links shown under "Explore" on the employee home.
 *
 * To add a new link, append an entry to QUICK_LINKS:
 *  - `action` decides what happens on click:
 *      route    → in-app route
 *      external → opens a URL in a new tab
 *      custom   → runs `run({ navigate, auth })`, for flows that need an API call first
 *  - `isVisible` decides who sees it. Omit it to show the link to everyone.
 *    For Even links use `canOpenEven` (feature flag + Even product + successful Even auth); for other
 *    provider-specific links use `hasProviderProduct`.
 *  - Even features open in-app at /even/<feature>; add new ones to EVEN_FEATURES in hooks/useEvenRedirect.
 * Links render in the order they appear here.
 *  - `group` decides the section a link sits under on the "View All" page (see QUICK_LINK_GROUPS).
 *
 * Only the first MAX_INLINE_QUICK_LINKS links show inline on mobile; once there are more than that,
 * a "View All" CTA opens the full Explore page (/explore) listing every link by group.
 */

export const MAX_INLINE_QUICK_LINKS = 5;

export const QUICK_LINK_GROUPS = [
  { id: 'insurance', title: 'Insurance' },
  { id: 'perks', title: 'Perks' },
  { id: 'healthcare', title: 'Healthcare' },
] as const;

export type QuickLinkGroupId = (typeof QUICK_LINK_GROUPS)[number]['id'];

export interface QuickLinkContext {
  userDetails: any;
  auth: any;
  brokerName?: string;
  // True once the backend has issued an Even auth token for this user.
  evenAuthenticated: boolean;
  hasEvenQuicklnks: boolean | null;
  hasGiftCards: boolean | null;
  hasORPAccess: boolean | null;
}

export interface QuickLinkHelpers {
  navigate: NavigateFunction;
  auth: any;
}

export type QuickLinkAction =
  | { type: 'route'; path: string }
  | { type: 'external'; url: string }
  | { type: 'custom'; run: (helpers: QuickLinkHelpers) => void | Promise<void> };

export interface QuickLink {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  group: QuickLinkGroupId;
  action: QuickLinkAction;
  isVisible?: (ctx: QuickLinkContext) => boolean;
}

// userDetails is data[0] of organisations/employee/organisation_member_detail.
// Member products (subscriptions) carry their provider at provider_product_mapping.provider.name.
// Matches the provider name as a whole word, case-insensitive ("Even", "EVEN", "Even Healthcare").
export const hasProviderProduct = (userDetails: any, providerName: string): boolean => {
  const pattern = new RegExp(`\\b${providerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  return (userDetails?.subscriptions || []).some((item: any) =>
    pattern.test(item?.provider_product_mapping?.provider?.name || '')
  );
};

export const hasEvenProduct = ({ userDetails }: Pick<QuickLinkContext, 'userDetails'>): boolean =>
  hasProviderProduct(userDetails, 'even');

// Even links require the organisation flag, an assigned Even product and a successful auth-token call.
const canOpenEven = (ctx: QuickLinkContext): boolean =>
  ctx.hasEvenQuicklnks === true && hasEvenProduct(ctx) && ctx.evenAuthenticated;

// Mirrors the Router: /gift-cards is only registered for ACTIVE members in employee mode.
const canOpenGiftCards = ({ auth, hasGiftCards }: QuickLinkContext): boolean =>
  hasGiftCards === true && auth?.status === 'ACTIVE' && auth?.activeMode === 'employee';

// Curated Deals is the former "Wellness" navbar item and keeps its visibility rule:
// the organisation has ORP (offers) access and the user is in employee mode.
const canOpenCuratedDeals = ({ auth, hasORPAccess }: QuickLinkContext): boolean =>
  hasORPAccess === true && auth?.activeMode === 'employee';

// Same eligibility as the home FTI banner (showFTI in MembersLayout + the FTI card rule in UpgradeCard):
// the org offers Flexi Top-up, the broker isn't Covrzy, and the member has no FTI policy/renewal in progress.
const canBuySuperTopup = ({ auth, brokerName }: QuickLinkContext): boolean => {
  const uhi = auth?.uhi;
  return (
    auth?.activeMode === 'employee' &&
    uhi?.renewal_number !== false &&
    !brokerName?.toLowerCase().includes('covrzy') &&
    !uhi?.show_grace &&
    !uhi?.show_renewal &&
    uhi?.policy_type === null
  );
};

// Same flow as the home FTI banner: resume a pending order if there is one, otherwise open the landing page.
const openSuperTopup = ({ navigate, auth }: QuickLinkHelpers) =>
  api.UHI.getFamilyMembers({ member_uuid: auth?.member_id })
    .then((res: any) => {
      if (res?.order_id) {
        navigate(`/upgrade-policy/post-payment?order_id=${res.order_id}&order_token=${res.order_token}`);
        successNotification(res?.message);
      } else {
        navigate('/upgrade-policy?utm_source=platform&utm_medium=quick_link&utm_campaign=home_explore');
      }
    })
    .catch((error: any) => errorHandler(error?.response?.data));

export const QUICK_LINKS: QuickLink[] = [
  {
    id: 'raise-claim',
    group: 'insurance',
    title: 'Raise Claim',
    subtitle: 'Submit or track your claim',
    icon: raiseClaimIcon,
    action: { type: 'route', path: '/claims/add-claim' },
  },
  // {
  //   id: 'super-top-up',
  //   group: 'insurance',
  //   title: 'Super Top-up',
  //   subtitle: 'Get extra health coverage',
  //   icon: superTopupIcon,
  //   action: { type: 'custom', run: openSuperTopup },
  //   isVisible: canBuySuperTopup,
  // },
  {
    id: 'gift-cards',
    group: 'perks',
    title: 'Gift Cards',
    subtitle: 'Pick your favourite gift cards',
    icon: giftCardsIcon,
    action: { type: 'route', path: '/gift-cards' },
    isVisible: canOpenGiftCards,
  },
  {
    id: 'curated-deals',
    group: 'perks',
    title: 'Curated Deals',
    subtitle: 'Discover exclusive deals',
    icon: curatedDealsIcon,
    action: { type: 'route', path: '/wellness/?utm_source=platform&utm_medium=quick_link&utm_campaign=employee_offer' },
    isVisible: canOpenCuratedDeals,
  },
  {
    id: 'buy-medicines',
    group: 'healthcare',
    title: 'Medicines',
    subtitle: 'Order medicines with ease',
    icon: buyMedicinesIcon,
    action: { type: 'route', path: '/even/buy-medicines' },
    isVisible: canOpenEven,
  },
  {
    id: 'book-lab-test',
    group: 'healthcare',
    title: 'Lab Test',
    subtitle: 'Schedule lab tests with ease',
    icon: bookLabTestIcon,
    action: { type: 'route', path: '/even/lab-tests' },
    isVisible: canOpenEven,
  },
  {
    id: 'consult-doctor',
    group: 'healthcare',
    title: 'Consult Doctor',
    subtitle: 'Talk to a doctor online',
    icon: consultDoctorIcon,
    action: { type: 'route', path: '/even/consult-doctor' },
    isVisible: canOpenEven,
  },
];
